import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { FileText, X, PenLine, Eye, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_CATEGORIES = ["Naval History", "Political Science", "Voting", "Military Affairs", "Radio", "Writing", "Books", "Music"];

export default function Essays() {
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [viewingEssay, setViewingEssay] = useState(null);
  const [expandedEssay, setExpandedEssay] = useState(null);

  const { hash } = useLocation();

  const handleReadEssay = (essay) => {
    setViewingEssay(essay);
    axios.post(`${API}/essays/${essay.id}/view`)
      .then(() => setEssays(prev => prev.map(e =>
        e.id === essay.id ? { ...e, views: (e.views ?? 0) + 1 } : e
      )))
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/essays`)
      .then(r => {
        setEssays(r.data);
        if (hash) {
          const slug = hash.replace("#", "");
          const match = r.data.find(e =>
            e.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") === slug
          );
          if (match) setViewingEssay(match);
        }
      })
      .catch(() => setEssays([]))
      .finally(() => setLoading(false));
  }, [hash]);

  const filtered = activeCategory === "All"
    ? essays
    : essays.filter(e => (e.categories || []).includes(activeCategory));

  const usedCategories = ["All", ...ALL_CATEGORIES.filter(c =>
    essays.some(e => (e.categories || []).includes(c))
  )];

  const viewerSrc = viewingEssay
    ? (() => {
        const docUrl = viewingEssay.pdf_url
          ? viewingEssay.pdf_url
          : `${API}/essays/${viewingEssay.id}/pdf`;
        const isDocx = docUrl.toLowerCase().includes(".docx");
        return isDocx
          ? `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(docUrl)}`
          : `${docUrl}#toolbar=0&navpanes=0&scrollbar=1`;
      })()
    : null;

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="Essays — Donald Stephen Inbody"
        description="Essays by Donald S. Inbody on naval history, political science, military voting, civil-military relations, and the craft of writing."
        canonical="https://inbody.net/essays"
        keywords="Donald Inbody essays, naval history, political science, military affairs, civil-military relations, voting, writing"
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline text-[#C5A059]/70 mb-4">Writing</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black text-[#F5F0E8] mb-6">
            Essays
          </h1>
          <p className="text-[#F5F0E8]/60 max-w-2xl leading-relaxed">
            Short-form writing on naval history, political science, military affairs, and the craft of writing. These are first drafts and not prepared for publication, but I offer them as my first thoughts on the subjects.
          </p>
        </div>
      </section>

      {/* Category filter */}
      {usedCategories.length > 1 && (
        <div className="border-b border-[#0A1628]/10 bg-[#F5F0E8] sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 flex gap-1 overflow-x-auto py-3">
            {usedCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`essay-category-${cat}`}
                className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat
                    ? "bg-[#0A1628] text-[#F5F0E8]"
                    : "text-[#2D3142] hover:bg-[#0A1628]/8"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Essays list */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 border border-[#0A1628]/10 bg-white">
              <PenLine size={28} className="text-[#C5A059] mx-auto mb-3" />
              <p className="font-serif text-xl text-[#0A1628] mb-2">No essays yet</p>
              <p className="text-[#2D3142]/60 text-sm">Check back soon — essays will appear here as they are posted.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map(essay => (
                <article
                  key={essay.id}
                  data-testid={`essay-card-${essay.id}`}
                  className="bg-white border border-[#0A1628]/10 p-6 lg:p-8 hover:border-[#C5A059]/40 transition-colors group"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-8">
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {(essay.categories || []).map(cat => (
                          <span key={cat} className="text-[10px] font-bold uppercase tracking-widest text-[#C5A059]/80 bg-[#C5A059]/8 px-2 py-0.5">
                            {cat}
                          </span>
                        ))}
                        <span className="text-[10px] font-mono text-[#2D3142]/40 bg-[#0A1628]/5 px-2 py-0.5">
                          {essay.year}
                        </span>
                      </div>
                      <h2 className="font-serif text-xl font-bold text-[#0A1628] mb-2 group-hover:text-[#C5A059] transition-colors">
                        {essay.title}
                      </h2>
                      <p className="text-[#2D3142]/70 text-sm leading-relaxed line-clamp-3">
                        {essay.abstract}
                      </p>
                      <div className="flex items-center gap-4 mt-4">
                        {essay.content && (
                          <button
                            onClick={() => {
                              const isOpen = expandedEssay === essay.id;
                              setExpandedEssay(isOpen ? null : essay.id);
                              if (!isOpen) {
                                axios.post(`${API}/essays/${essay.id}/view`)
                                  .then(() => setEssays(prev => prev.map(e =>
                                    e.id === essay.id ? { ...e, views: (e.views ?? 0) + 1 } : e
                                  )))
                                  .catch(() => {});
                              }
                            }}
                            data-testid={`read-essay-${essay.id}`}
                            className="inline-flex items-center gap-2 text-xs font-semibold text-[#0A1628] border border-[#0A1628] px-4 py-2 hover:bg-[#0A1628] hover:text-[#F5F0E8] transition-colors"
                          >
                            {expandedEssay === essay.id
                              ? <><ChevronUp size={12} /> Collapse</>
                              : <><ChevronDown size={12} /> Read Essay</>
                            }
                          </button>
                        )}
                        {!essay.content && essay.has_pdf && (
                          <button
                            onClick={() => handleReadEssay(essay)}
                            data-testid={`read-essay-pdf-${essay.id}`}
                            className="inline-flex items-center gap-2 text-xs font-semibold text-[#0A1628] border border-[#0A1628] px-4 py-2 hover:bg-[#0A1628] hover:text-[#F5F0E8] transition-colors"
                          >
                            <FileText size={12} /> Read Essay
                          </button>
                        )}
                        {(essay.views ?? 0) > 0 && (
                          <span className="flex items-center gap-1 text-[#2D3142]/35 text-xs">
                            <Eye size={11} />
                            {essay.views} {essay.views === 1 ? "read" : "reads"}
                          </span>
                        )}
                      </div>
                      {/* Inline essay content */}
                      {expandedEssay === essay.id && essay.content && (
                        <div
                          className="mt-6 pt-6 border-t border-[#0A1628]/10 prose prose-sm max-w-none text-[#2D3142] leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(essay.content) }}
                        />
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Document viewer modal */}
      {viewingEssay && (
        <div
          data-testid="essay-viewer-modal"
          className="fixed inset-0 z-50 bg-black/80 flex flex-col"
          onClick={e => { if (e.target === e.currentTarget) setViewingEssay(null); }}
        >
          <div className="bg-[#0A1628] px-6 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <FileText size={16} className="text-[#C5A059]" />
              <span className="font-serif text-[#F5F0E8] font-semibold text-sm line-clamp-1">
                {viewingEssay.title}
              </span>
            </div>
            <button
              onClick={() => setViewingEssay(null)}
              data-testid="close-essay-viewer"
              className="text-[#F5F0E8]/60 hover:text-[#F5F0E8] transition-colors p-1"
            >
              <X size={18} />
            </button>
          </div>
          <iframe
            src={viewerSrc}
            title={viewingEssay.title}
            className="flex-1 w-full border-0"
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
